import React, { useCallback, useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
const WHATSAPP_URL = 'https://wa.me/77010346443?text=%D1%85%D0%BE%D1%87%D1%83%20%D0%B4%D0%B5%D0%BC%D0%BE%20Meelo';
const SECTIONS = [{
  id: 'what-is-meelo',
  label: 'О Meelo'
}, {
  id: 'solutions',
  label: 'Решения'
}, {
  id: 'contact',
  label: 'Связаться с нами'
}];
const NAV_LINKS = [...SECTIONS.map(s => ({
  href: `#${s.id}`,
  label: s.label,
  external: false
})), {
  href: '#/mission',
  label: 'Миссия',
  external: false
}];
function getActiveSection(): string {
  const offset = 120;
  let active = '';
  for (const {
    id
  } of SECTIONS) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el.getBoundingClientRect().top <= offset) active = id;
  }
  return active;
}
function getScrollProgress(): number {
  const scrollTop = window.scrollY;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (docHeight <= 0) return 0;
  return Math.min(100, scrollTop / docHeight * 100);
}
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 10);
    setScrollProgress(getScrollProgress());
    setActiveSection(getActiveSection());
  }, []);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  const linkClass = (id: string) => activeSection === id ? 'text-sm font-medium text-[#7C4DFF] bg-[#7C4DFF]/10 rounded-full px-3 py-1 transition-colors whitespace-nowrap' : 'text-sm font-medium text-gray-700 hover:text-[#7C4DFF] transition-colors whitespace-nowrap px-3 py-1';
  const close = () => setIsMobileMenuOpen(false);
  return <>
      {/* Scroll progress bar */}
      <div aria-hidden="true" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      height: 3,
      width: `${scrollProgress}%`,
      background: '#7C4DFF',
      zIndex: 100,
      transition: prefersReducedMotion ? 'none' : 'width 80ms linear',
      pointerEvents: 'none'
    }} data-id="element-42" />

      {/* ── Full-screen mobile overlay ─────────────────────────────────── */}
      <div aria-hidden={!isMobileMenuOpen} style={{
      position: 'fixed',
      inset: 0,
      zIndex: 80,
      background: 'rgba(255,255,255,0.88)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(-100%)',
      opacity: isMobileMenuOpen ? 1 : 0,
      transition: prefersReducedMotion ? 'none' : 'transform 280ms cubic-bezier(0.4,0,0.2,1), opacity 280ms ease',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} className="md:hidden" data-id="element-43">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4" data-id="element-44">
          <a href="#" onClick={close} data-id="element-45">
            <img src="https://cdn.magicpatterns.com/uploads/k1HQQhYUAYkCqHQP8zEE8y/meelo_logo_transparent.png" alt="Meelo" className="h-10 w-auto" data-id="element-46" />
          </a>
          <button onClick={close} aria-label="Закрыть меню" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#EDE7F6] hover:text-[#7C4DFF] transition-all" style={{
          transform: isMobileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: prefersReducedMotion ? 'none' : 'transform 280ms ease'
        }} data-id="element-47">
            <X size={20} data-id="element-48" />
          </button>
        </div>

        {/* Thin divider */}
        <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" data-id="element-49" />

        {/* Nav links */}
        <nav className="flex-1 flex flex-col justify-center px-8 gap-1" data-id="element-50">
          {NAV_LINKS.map(({
          href,
          label
        }) => <a key={href} href={href} onClick={close} className="group flex items-center py-4 text-2xl font-semibold text-[#1A1A2E] hover:text-[#7C4DFF] transition-colors border-b border-gray-100 last:border-0" style={{
          letterSpacing: '-0.01em'
        }} data-id="element-51">
              <span className="transition-transform duration-150 group-active:scale-[1.02]" data-id="element-52">
                {label}
              </span>
              <span className="ml-auto text-gray-300 group-hover:text-[#7C4DFF]/40 transition-colors text-lg" data-id="element-53">
                →
              </span>
            </a>)}
        </nav>

        {/* Phone / CTA */}
        <div className="px-8 pb-10 pt-6 border-t border-gray-100" data-id="element-54">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={close} className="block" data-id="element-55">
            <p className="text-2xl font-bold text-[#7C4DFF] tracking-tight mb-1" data-id="element-56">
              +7 701 034 64 43
            </p>
            <p className="text-sm text-gray-400 font-medium" data-id="element-57">
              Написать в WhatsApp
            </p>
          </a>
        </div>
      </div>

      {/* ── Pill navbar ───────────────────────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4 sm:pt-5" data-id="element-58">
        <nav className={`w-full max-w-[1000px] transition-all duration-300 rounded-full border border-white/30 shadow-[0_1px_12px_-2px_rgba(0,0,0,0.06)] backdrop-blur-2xl ${isScrolled ? 'bg-white/30' : 'bg-white/20'}`} data-id="element-59">
          <div className="flex items-center h-14 px-5 sm:px-7" data-id="element-60">
            <a href="#" className="flex-shrink-0 mr-auto md:mr-0" data-id="element-61">
              <img src="https://cdn.magicpatterns.com/uploads/k1HQQhYUAYkCqHQP8zEE8y/meelo_logo_transparent.png" alt="Meelo" className="h-10 sm:h-12 w-auto" data-id="element-62" />
            </a>

            {/* Desktop links */}
            <div className="hidden md:flex items-center justify-end flex-1 gap-1" data-id="element-63">
              {SECTIONS.map(({
              id,
              label
            }) => <a key={id} href={`#${id}`} className={linkClass(id)} data-id="element-64">
                  {label}
                </a>)}
              <a href="#/mission" className="text-sm font-medium text-gray-700 hover:text-[#7C4DFF] transition-colors whitespace-nowrap px-3 py-1" data-id="element-65">
                Миссия
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#7C4DFF] hover:text-[#651FFF] transition-colors whitespace-nowrap px-3 py-1 ml-4" data-id="element-66">
                +7 701 034 64 43
              </a>
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden text-gray-600 hover:text-[#7C4DFF] transition-colors" aria-label="Открыть меню" data-id="element-67">
              <Menu size={22} data-id="element-68" />
            </button>
          </div>
        </nav>
      </div>
    </>;
}